import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../components/atoms/Icon';
import Header from '../components/organisms/Header';
import { updateCandidateProfile, getCandidateProfile, parseResumeFile } from '../services/api';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL } from '../config';

const skillOptions = [
  'JavaScript', 'TypeScript', 'React', 'Node.js', 'Python', 'Java',
  'C++', 'Go', 'Rust', 'PHP', 'Ruby', 'Swift', 'Kotlin', 'SQL',
  'MongoDB', 'PostgreSQL', 'AWS', 'Docker', 'Kubernetes', 'Git',
  'Project Management', 'Team Leadership', 'Agile', 'Scrum',
  'UI/UX Design', 'Data Analysis', 'Machine Learning', 'DevOps',
  'Angular', 'Vue', 'Django', 'Flask', 'Spring', 'Laravel',
  'HTML', 'CSS', 'SASS', 'SCSS', 'Figma', 'Adobe', 'Jira',
  'Jenkins', 'Terraform', 'TensorFlow', 'PyTorch', 'Pandas',
  'Numpy', 'Scikit-learn', 'R', 'MATLAB', 'Tableau', 'Excel',
  'Testing', 'Selenium', 'Jest', 'JUnit', 'Security', 'Linux',
  'Windows', 'PowerShell', 'Bash', 'API', 'REST', 'GraphQL',
  'Microservices', 'Serverless', 'Mobile Development', 'React Native',
  'Flutter', 'Blockchain', 'Ethereum', 'Solidity', 'IoT',
  'Arduino', 'Raspberry Pi', 'Unity', 'Unreal Engine', '3D Modeling',
  'Robotics', 'Control Systems', 'Bioinformatics', 'Statistics',
  'Financial Modeling', 'ETL', 'Spark', 'Monitoring', 'Architecture',
  'Design Patterns', 'Business Intelligence', 'Technical Writing',
  'Documentation', 'Customer Support', 'Troubleshooting',
  'Communication', 'Presentation', 'Research', 'Biology',
  'Chemistry', 'Physics', 'Mathematics', 'Economics', 'Finance',
  'Marketing', 'Sales', 'Human Resources', 'Legal', 'Healthcare',
  'Education', 'Government', 'Non-profit', 'Consulting', 'Freelance'
];

const jobRoleOptions = [
  'Frontend Developer', 'Backend Developer', 'Full Stack Developer',
  'Data Scientist', 'DevOps Engineer', 'Project Manager',
  'UI/UX Designer', 'Mobile Developer', 'Software Engineer',
  'Machine Learning Engineer', 'Data Analyst', 'QA Engineer',
  'System Administrator', 'Database Administrator', 'Network Engineer',
  'Security Engineer', 'Cloud Engineer', 'Product Manager',
  'Business Analyst', 'Sales Engineer', 'Technical Writer',
  'Support Engineer', 'Research Scientist', 'Embedded Engineer',
  'Game Developer', 'Blockchain Developer', 'AI Engineer',
  'Robotics Engineer', 'Bioinformatics Scientist', 'Quantitative Analyst',
  'Site Reliability Engineer', 'Platform Engineer', 'Data Engineer',
  'MLOps Engineer', 'Frontend Engineer', 'Backend Engineer',
  'Software Architect', 'Engineering Manager', 'CTO',
  'VP Engineering', 'Director of Engineering'
];

const MAX_UPLOAD_SIZE = 5 * 1024 * 1024; // 5MB

const ProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    jobRole: '',
    skills: [],
    resume: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [status, setStatus] = useState(null);

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [resumeFileName, setResumeFileName] = useState(null); // Track resume filename separately
  const [skillSearch, setSkillSearch] = useState('');
  const [parsedResumeData, setParsedResumeData] = useState(null); // Store parsed resume data
  const [showParsedView, setShowParsedView] = useState(false); // Toggle between form and parsed view
  const [verificationStatus, setVerificationStatus] = useState(null); // Verification status

  // Load existing profile data function
  const loadProfile = async () => {
    try {
      setLoadingProfile(true);
      
      // Get candidate session data using new session system
      const candidateSession = SessionManager.getSession('candidate');
      const candidateId = candidateSession?.user_id;
      const loginEmail = candidateSession?.email || '';
      
      console.log('loadProfile called with:', { candidateId, loginEmail });
      
      if (!candidateId && !loginEmail) {
        console.log('No candidate ID or email found in session');
        setLoadingProfile(false);
        return;
      }
      
      // Always set email to login email first (before API call)
      setFormData((prev) => ({
        ...prev,
        email: loginEmail, // Always use login email
      }));
      
      try {
        const profile = await getCandidateProfile(candidateId, loginEmail);
        console.log('Profile received from API:', profile);
        
        if (profile) {
          // Check if profile has complete data (education, work experience, or skills)
          const hasCompleteProfile = profile.full_name && profile.phone && (
            (profile.education && profile.education.length > 0) ||
            (profile.work_experience && profile.work_experience.length > 0) ||
            (profile.skills && profile.skills.length > 0)
          );
          
          // Check if user explicitly wants to edit
          const searchParams = new URLSearchParams(location.search);
          const forceEdit = searchParams.get('edit') === 'true';
          
          // If user has complete profile and not forcing edit, redirect to profile view
          if (hasCompleteProfile && !forceEdit) {
            console.log('User has complete profile, redirecting to profile view');
            navigate('/profile/view');
            return;
          }
          
          // Always load fresh data from API, don't merge with potentially stale form data
          setFormData({
            fullName: profile.first_name && profile.last_name 
              ? `${profile.first_name} ${profile.last_name}`.trim()
              : profile.full_name || '',
            email: loginEmail, // Always use login email
            phone: profile.phone_number || profile.phone || '',
            jobRole: profile.job_role || '',
            skills: Array.isArray(profile.skills) ? profile.skills : [],
            resume: null, // Resume file is not loaded, only URL is stored
          });
          
          // Store resume filename if it exists (always update this)
          if (profile.resume_filename) {
            setResumeFileName(profile.resume_filename);
            console.log('Resume filename set:', profile.resume_filename);
          } else {
            setResumeFileName(null);
          }
          console.log('Profile loaded and form updated successfully');
        } else {
          console.log('Profile is null or undefined');
        }
      } catch (profileError) {
        // If profile doesn't exist (404), that's okay - user can create one
        const errorMessage = profileError?.message || String(profileError);
        console.error('Error fetching profile:', profileError);
        if (errorMessage.includes('404') || errorMessage.includes('not found')) {
          console.log('Profile not found (404), user can create one');
        } else {
          console.error('Unexpected error loading profile:', profileError);
        }
      }
    } catch (error) {
      console.error('Error in loadProfile function:', error);
      // Don't clear existing form data if there's an error
    } finally {
      setLoadingProfile(false);
    }
  };

  // Check authentication and load existing profile data
  useEffect(() => {
    // Try to migrate legacy session first
    SessionManager.migrateLegacySession();
    
    // Check if user is logged in as candidate using new session system
    const isLoggedInAsCandidate = SessionManager.isLoggedIn('candidate');
    const currentSession = SessionManager.getCurrentSession();
    
    // If not logged in as candidate, redirect to login page
    if (!isLoggedInAsCandidate || (currentSession && currentSession.role !== 'candidate')) {
      navigate('/login');
      return;
    }

    // Get candidate session data
    const candidateSession = SessionManager.getSession('candidate');
    if (candidateSession && candidateSession.email) {
      setFormData((prev) => ({
        ...prev,
        email: candidateSession.email, // Use session email
      }));
    }

    // Check if user explicitly wants to edit (via query param)
    const searchParams = new URLSearchParams(location.search);
    const forceEdit = searchParams.get('edit') === 'true';
    
    // Always load profile data when component mounts
    console.log('ProfilePage mounted, loading profile data', { forceEdit });
    loadProfile();
    
    // Load verification status
    const fetchVerificationStatus = async () => {
      try {
        const candidateSession = SessionManager.getSession('candidate');
        const candidateId = candidateSession?.user_id;
        const token = localStorage.getItem('token');
        
        if (!candidateId) return;
        
        const response = await fetch(`${API_BASE_URL}/verification/status/${candidateId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setVerificationStatus(data);
        }
      } catch (error) {
        console.error('Error fetching verification status:', error);
      }
    };
    
    fetchVerificationStatus();
    
    // Don't reload on window focus - it causes issues with file uploads
    // The profile will be loaded on mount and after successful updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - component will remount when navigating back due to key prop

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSkillToggle = (skill) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter((s) => s !== skill)
        : [...prev.skills, skill],
    }));
  };

  // Filter skills based on search
  const filteredSkills = skillOptions.filter(skill =>
    skill.toLowerCase().includes(skillSearch.toLowerCase())
  );

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, 'Type:', file.type, 'Size:', file.size);

    // Check file type - allow PDF and octet-stream (some browsers send PDF as octet-stream)
    if (file.type !== 'application/pdf' && file.type !== 'application/octet-stream' && !file.name.toLowerCase().endsWith('.pdf')) {
      setStatus({
        type: 'error',
        title: 'Invalid File Type',
        description: 'Please upload a PDF file only.',
      });
      // Reset file input
      e.target.value = '';
      return;
    }

    if (file.size > MAX_UPLOAD_SIZE) {
      setStatus({
        type: 'error',
        title: 'File Too Large',
        description: 'Please upload a file smaller than 5MB.',
      });
      // Reset file input
      e.target.value = '';
      return;
    }

    setFormData((prev) => {
      const updated = { ...prev, resume: file };
      console.log('Setting resume in formData:', file.name);
      return updated;
    });
    setResumeFileName(file.name);
    
    // Parse the resume automatically
    try {
      setStatus({
        type: 'info',
        title: 'Parsing Resume',
        description: 'Extracting information from your resume...',
      });
      
      const parseResult = await parseResumeFile(file);
      console.log('Resume parsed successfully:', parseResult);
      
      if (parseResult.success && parseResult.data) {
        setParsedResumeData(parseResult.data);
        
        // Auto-fill form fields from parsed data
        const personalInfo = parseResult.data.personalInfo || {};
        const skills = parseResult.data.skills || [];
        
        setFormData((prev) => ({
          ...prev,
          fullName: personalInfo.name || prev.fullName,
          phone: personalInfo.phone || prev.phone,
          skills: skills.length > 0 ? skills : prev.skills,
        }));
        
        setStatus({
          type: 'success',
          title: 'Resume Parsed Successfully',
          description: `Extracted ${skills.length} skills and other information. You can view the parsed profile or continue editing.`,
        });
      }
    } catch (error) {
      console.error('Error parsing resume:', error);
      setStatus({
        type: 'warning',
        title: 'Resume Selected',
        description: `${file.name} is ready to upload. Could not auto-extract data: ${error.message}`,
      });
    }
    
    console.log('Resume file set in formData');
  };
  

  const validateForm = () => {
    // Resume is optional if one was already uploaded (resumeFileName exists)
    const resumeRequired = !resumeFileName;
    
    if (
      !formData.fullName ||
      !formData.email ||
      !formData.phone ||
      !formData.jobRole ||
      formData.skills.length === 0 ||
      (resumeRequired && !formData.resume)
    ) {
      setStatus({
        type: 'error',
        title: 'Missing Information',
        description: resumeRequired 
          ? 'Please complete all fields and upload your resume.'
          : 'Please complete all required fields.',
      });
      return false;
    }
    return true;
  };

  const simulateUpload = () => {
    setUploadProgress(0);
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            resolve();
            return 100;
          }
          return prev + Math.random() * 15;
        });
      }, 200);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      // Get candidate ID from session (set during login)
      const candidateSession = SessionManager.getSession('candidate');
      let candidateId = candidateSession?.user_id;
      
      // Always use the login email from session - this is the authenticated email
      const loginEmail = candidateSession?.email || '';
      
      // Override formData.email with login email to ensure consistency
      const formDataToSubmit = {
        ...formData,
        email: loginEmail, // Always use login email, ignore any changes to email field
      };
      
      // If no candidate ID found, don't send it - backend will look up by email
      // Only send candidate_id if it's a valid candidate ID (starts with 'cand-')
      if (candidateId && !candidateId.startsWith('cand-') && !candidateId.startsWith('hr-')) {
        // This is not a valid candidate ID, might be an email or other value
        candidateId = null;
      }
      
      if (!loginEmail) {
        setStatus({
          type: 'error',
          title: 'Authentication Error',
          description: 'Please log in again to update your profile.',
        });
        setIsSubmitting(false);
        return;
      }

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 15;
        });
      }, 200);

      // Upload resume and save profile data (using login email)
      console.log('Submitting profile update:', {
        candidateId,
        hasResume: !!formDataToSubmit.resume,
        resumeName: formDataToSubmit.resume?.name,
        formData: {
          fullName: formDataToSubmit.fullName,
          email: formDataToSubmit.email,
          phone: formDataToSubmit.phone,
          jobRole: formDataToSubmit.jobRole,
          skillsCount: formDataToSubmit.skills?.length || 0,
        }
      });
      
      console.log('🔍 Exact form data being sent:', {
        'full_name will be': formDataToSubmit.fullName,
        'email will be': formDataToSubmit.email,
        'phone will be': formDataToSubmit.phone,
        'job_role will be': formDataToSubmit.jobRole,
        'skills will be': formDataToSubmit.skills
      });
      
      await updateCandidateProfile(candidateId, formDataToSubmit);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      console.log('Profile update successful');

      // Clear any cached profile data to force fresh fetch on next load
      SessionManager.clearProfileCache('candidate');
      console.log('✅ Profile updated successfully, clearing cached data');

      // Reload profile data from API to get the updated information
      try {
        console.log('🔄 Fetching updated profile from API...');
        const updatedProfile = await getCandidateProfile(candidateId, loginEmail);
        console.log('🔄 Fetched updated profile after save:', updatedProfile);
        console.log('🔍 Profile name fields:', {
          'first_name': updatedProfile?.first_name,
          'last_name': updatedProfile?.last_name,
          'full_name': updatedProfile?.full_name,
          'what we sent': formDataToSubmit.fullName
        });
        
        if (updatedProfile) {
          // Update the session with the fresh profile data using the new method
          const profileUpdateData = {
            ...updatedProfile,
            // Ensure full_name is properly set for Header display
            full_name: updatedProfile.first_name && updatedProfile.last_name 
              ? `${updatedProfile.first_name} ${updatedProfile.last_name}`.trim()
              : updatedProfile.full_name
          };
          
          console.log('🔄 Profile data to update session with:', profileUpdateData);
          console.log('🔍 Name resolution:', {
            'has first_name': !!updatedProfile.first_name,
            'has last_name': !!updatedProfile.last_name,
            'constructed name': updatedProfile.first_name && updatedProfile.last_name 
              ? `${updatedProfile.first_name} ${updatedProfile.last_name}`.trim()
              : 'not constructed',
            'existing full_name': updatedProfile.full_name,
            'final full_name': profileUpdateData.full_name
          });
          
          SessionManager.updateSessionProfile('candidate', profileUpdateData);
          console.log('🔄 Updated session with fresh profile data:', profileUpdateData);
          
          // Update form data with fresh API data
          setFormData({
            fullName: updatedProfile.first_name && updatedProfile.last_name 
              ? `${updatedProfile.first_name} ${updatedProfile.last_name}`.trim()
              : updatedProfile.full_name || '',
            email: loginEmail, // Always use login email
            phone: updatedProfile.phone_number || updatedProfile.phone || '',
            jobRole: updatedProfile.job_role || '',
            skills: Array.isArray(updatedProfile.skills) ? updatedProfile.skills : [],
            resume: null, // Resume file is not loaded, only URL is stored
          });
          
          // Update resume filename if it exists
          if (updatedProfile.resume_filename) {
            setResumeFileName(updatedProfile.resume_filename);
          }
        }
      } catch (profileError) {
        console.error('Error fetching updated profile:', profileError);
        // Still show success message even if we can't fetch updated profile
      }

      setStatus({
        type: 'success',
        title: 'Profile Updated Successfully',
        description: (
          <div>
            <p>Your resume and skills have been saved to your profile.</p>
            {parsedResumeData && (
              <button
                onClick={() => navigate('/profile/view')}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700 underline"
              >
                View Parsed Profile →
              </button>
            )}
          </div>
        ),
      });

      // Update resume filename if a new file was uploaded
      if (formData.resume) {
        setResumeFileName(formData.resume.name);
        // Clear the file input so it doesn't show as selected
        setFormData((prev) => ({ ...prev, resume: null }));
      }
      
      setUploadProgress(0);
      setIsSubmitting(false);
    } catch (error) {
      setStatus({
        type: 'error',
        title: 'Upload Failed',
        description: error.message || 'Failed to upload resume. Please try again.',
      });
      setUploadProgress(0);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="relative flex min-h-screen flex-col bg-background-light dark:bg-background-dark">
        <Header />
        <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center py-20">
              <p className="text-gray-600 dark:text-gray-400">Loading profile...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-background-light dark:bg-background-dark">
      <Header />
      <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-left">
            <div className="flex items-center gap-3 mb-2">
              <p className="text-sm uppercase tracking-[0.3em] text-primary">
                My Profile
              </p>
              {verificationStatus?.verified && (
                <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 px-3 py-1 rounded-full">
                  <Icon name="verified" size={16} className="text-green-600" />
                  <span className="text-xs font-semibold text-green-600">Verified</span>
                </div>
              )}
            </div>
            <h1 className="text-3xl font-black text-gray-900 dark:text-white leading-tight">
              Candidate Profile {resumeFileName && <span className="text-lg font-normal text-gray-500">(Editing)</span>}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 mt-2 max-w-xl">
              {resumeFileName 
                ? 'Update your profile information and resume. Changes will be reflected in your profile view.'
                : 'Upload your resume and complete your profile. HR teams can view your profile to match you with opportunities.'
              }
            </p>
          </div>
          <div className="flex flex-col gap-2">
            {resumeFileName && (
              <button
                onClick={() => navigate('/profile/view')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 whitespace-nowrap"
              >
                <Icon name="visibility" />
                View Profile
              </button>
            )}
            {!verificationStatus?.verified && resumeFileName && (
              <button
                onClick={() => navigate('/verification-welcome')}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 flex items-center gap-2 whitespace-nowrap"
              >
                <Icon name="verified_user" size={20} />
                Verify Profile
              </button>
            )}
          </div>
        </div>

        {status && (
          <div
            className={`mb-6 rounded-xl border px-4 py-3 flex items-start gap-3 ${
              status.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : status.type === 'error'
                ? 'border-red-200 bg-red-50 text-red-800'
                : 'border-blue-200 bg-blue-50 text-blue-800'
            }`}
          >
            <Icon
              name={
                status.type === 'success'
                  ? 'check_circle'
                  : status.type === 'error'
                  ? 'error'
                  : 'info'
              }
            />
            <div>
              <p className="font-semibold">{status.title}</p>
              <p className="text-sm">{status.description}</p>
            </div>
          </div>
        )}

        <div className="bg-white/90 dark:bg-gray-900/60 backdrop-blur-sm rounded-3xl shadow-2xl p-6 sm:p-8 border border-gray-200 dark:border-gray-800">
          <form onSubmit={handleSubmit} className="space-y-10">
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <Icon name="badge" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Personal Information
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Job Role *
                  </label>
                  <select
                    name="jobRole"
                    value={formData.jobRole}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                    required
                  >
                    <option value="">Select a job role...</option>
                    {jobRoleOptions.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                    <option value="other">Other (specify below)</option>
                  </select>
                  {formData.jobRole === 'other' && (
                    <input
                      type="text"
                      name="customRole"
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          jobRole: e.target.value,
                        }))
                      }
                      className="mt-3 w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      placeholder="Enter your specific job role"
                      required
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Icon name="mail" className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email || SessionManager.getSession('candidate')?.email || ''}
                      readOnly
                      disabled
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                      placeholder="your.email@example.com"
                      title="Email cannot be changed. It is linked to your login account."
                    />
                    <div className="absolute right-3 top-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400" title="Email is linked to your login account">
                        (Locked)
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Email is linked to your login account and cannot be changed.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Icon name="call" className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      placeholder="+1 (555) 123-4567"
                      required
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                  <Icon name="code" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Skills ({formData.skills.length} selected)
                </h2>
              </div>

              {/* Search Input */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="search" className="text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search skills..."
                  value={skillSearch}
                  onChange={(e) => setSkillSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                />
              </div>

              {/* Selected Skills */}
              {formData.skills.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Selected Skills:</h3>
                  <div className="flex flex-wrap gap-2">
                    {formData.skills.map((skill) => (
                      <span
                        key={skill}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/40"
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => handleSkillToggle(skill)}
                          className="ml-1 hover:text-red-500 transition-colors"
                        >
                          <Icon name="close" className="text-xs" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Skills List Box - Only show when searching */}
              {skillSearch.trim() && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 max-h-64 overflow-y-auto">
                  {filteredSkills.length > 0 ? (
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                      {filteredSkills.map((skill) => (
                        <button
                          key={skill}
                          type="button"
                          onClick={() => handleSkillToggle(skill)}
                          className={`w-full px-4 py-3 text-left text-sm transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                            formData.skills.includes(skill)
                              ? 'bg-primary/5 text-primary font-medium border-r-2 border-primary'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{skill}</span>
                            {formData.skills.includes(skill) && (
                              <Icon name="check" className="text-primary text-sm" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      <Icon name="search" className="mx-auto mb-2 text-2xl" />
                      <p>No skills found matching "{skillSearch}"</p>
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center">
                  <Icon name="description" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Resume Upload
                </h2>
              </div>

              <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-2xl p-8 text-center hover:border-primary/60 transition-colors duration-200">
                <input
                  type="file"
                  id="resume"
                  name="resume"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label 
                  htmlFor="resume" 
                  className="cursor-pointer flex flex-col items-center space-y-4"
                >
                  <div className="p-4 bg-primary/10 rounded-full">
                    <Icon name="cloud_upload" className="text-primary text-2xl" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {formData.resume ? formData.resume.name : resumeFileName || 'Upload or update your resume'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {resumeFileName && !formData.resume ? `Current resume: ${resumeFileName}` : 'PDF files only, up to 5MB'}
                    </p>
                  </div>
                </label>

                {(formData.resume || resumeFileName) && (
                  <div className="mt-4 flex items-center justify-center text-emerald-600">
                    <Icon name="check_circle" className="mr-2" />
                    <span className="text-sm font-semibold">
                      {formData.resume ? 'New file selected' : 'Resume uploaded'}
                    </span>
                  </div>
                )}
              </div>
            </section>

            {isSubmitting && (
              <section className="space-y-3">
                <div className="flex items-center justify-between text-sm font-medium text-gray-600 dark:text-gray-300">
                  <span>Uploading...</span>
                  <span>{Math.round(uploadProgress)}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-800 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-primary to-emerald-500 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(uploadProgress, 100)}%` }}
                  ></div>
                </div>
              </section>
            )}

            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-primary to-indigo-600 text-white font-semibold py-4 px-8 rounded-2xl shadow-lg shadow-primary/30 hover:shadow-primary/50 focus:outline-none focus:ring-4 focus:ring-primary/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Updating Profile…' : 'Update Profile'}
              </button>
              
              {/* Temporary debug button */}
              <button
                type="button"
                onClick={() => {
                  console.log('🔧 Manual session refresh triggered');
                  const candidateSession = SessionManager.getSession('candidate');
                  console.log('🔍 Current session before refresh:', candidateSession);
                  
                  // Force trigger session update event
                  window.dispatchEvent(new CustomEvent('sessionUpdated', { 
                    detail: { role: 'candidate', sessionData: candidateSession } 
                  }));
                  
                  console.log('✅ Session update event dispatched manually');
                }}
                className="w-full mt-2 bg-gray-500 text-white font-semibold py-2 px-4 rounded-xl hover:bg-gray-600 transition-all"
              >
                🔧 Debug: Force Header Refresh
              </button>
            </div>
          </form>
        </div>
      </div>
      </main>
    </div>
  );
};

export default ProfilePage;
